import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { UserPlus, Zap, Heart, ShieldCheck, Droplets } from 'lucide-react';
import { Card, Button, BloodTypeBadge, ComponentBadge, UrgencyBadge, Reveal, Counter, Skeleton, useIsMobile } from '../components/ui';
import PageLayout from '../components/layout/PageLayout';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

const STEPS = [
  { Icon: UserPlus,   title: 'Register in a minute',   desc: 'Sign up as a donor, recipient, or organization — no paperwork.' },
  { Icon: Zap,         title: 'Get matched instantly',  desc: 'We match urgent requests to eligible donors nearby by blood type.' },
  { Icon: Heart,        title: 'Respond & save a life', desc: 'Accept a request, show up, and someone gets the blood they need.' },
  { Icon: ShieldCheck, title: 'Verified & secure',       desc: 'Organizations are admin-verified and your data stays private.' },
];

const HERO_TRUST = ['🔒 Encrypted & private', '✅ Admin-verified organizations', '⚡ Real-time matching'];

export default function Home() {
  const { user, loading: authLoading, becomeDonor } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(true);
  const [requestsError, setRequestsError] = useState(false);
  const [hoveredId, setHoveredId] = useState(null);
  const [stats, setStats] = useState(null);
  const [donorProfileMissing, setDonorProfileMissing] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const [upgradeError, setUpgradeError] = useState('');
  const isMobile = useIsMobile();

  // A donor account with no profile can't be matched to anything, so the hero
  // should nudge them to finish rather than send them browsing. 404 here is the
  // expected "not set up yet" answer, not a failure.
  useEffect(() => {
    if (user?.role !== 'donor') { setDonorProfileMissing(false); return; }
    api.get('/donors/profile')
      .then(() => setDonorProfileMissing(false))
      .catch(e => setDonorProfileMissing(e.response?.status === 404));
  }, [user]);

  const upgradeToDonor = async () => {
    setUpgrading(true);
    setUpgradeError('');
    try {
      await becomeDonor();
      navigate('/donor/setup?redirect=/donor/profile');
    } catch (e) {
      setUpgradeError(e.response?.data?.detail || 'Could not switch your account to a donor account.');
      setUpgrading(false);
    }
  };

  // Kept separate from the empty case on purpose. A failed load used to fall
  // through to "No open requests right now." — telling a visitor nobody needs
  // blood when in fact nothing loaded, which is the worst possible thing for
  // this page to get wrong.
  const loadRequests = useCallback(() => {
    setRequestsLoading(true);
    setRequestsError(false);
    api.get('/requests/', { params: { status: 'open' } })
      .then(r => setRequests(r.data))
      .catch(() => { setRequests([]); setRequestsError(true); })
      .finally(() => setRequestsLoading(false));
  }, []);

  useEffect(() => {
    loadRequests();

    // Stats failing is cosmetic — the counters just don't render.
    api.get('/stats/platform').then(r => setStats(r.data)).catch(() => setStats(null));
  }, [loadRequests]);

  // The API returns newest-first, which buries a critical request under a
  // routine one posted minutes later — fine when this sat below the fold, wrong
  // now that it leads the page. Sorted here rather than in the API so /requests
  // and the mobile app keep their existing chronological order.
  const URGENCY_RANK = { critical: 0, high: 1, normal: 2 };
  const liveRequests = [...requests]
    .sort((a, b) =>
      (URGENCY_RANK[a.urgency] ?? 9) - (URGENCY_RANK[b.urgency] ?? 9)
      || new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 4);

  const demandByType = requests.reduce((acc, r) => {
    acc[r.blood_type] = (acc[r.blood_type] || 0) + 1;
    return acc;
  }, {});
  const maxDemand = Math.max(1, ...Object.values(demandByType));
  const demandEntries = Object.entries(demandByType).sort((a, b) => b[1] - a[1]);

  // The hero speaks to whoever is looking at it: a donor doesn't need to
  // "become a donor", and an org/admin account can't (the upgrade is
  // recipient-only server-side).
  const hero = (() => {
    if (!user) return {
      subcopy: 'Register as a donor, respond to urgent requests, or find a donation camp near you.',
      primary: { label: '🩸 Become a Donor', to: '/register?role=donor' },
      secondary: { label: '+ Post a Request', to: `/register?redirect=${encodeURIComponent('/requests/new')}` },
    };
    if (user.role === 'donor') return donorProfileMissing ? {
      subcopy: "You're almost there — finish your donor profile so we can match you to requests nearby.",
      primary: { label: '🩸 Complete my donor profile', to: '/donor/setup' },
      secondary: { label: '+ Post a Request', to: '/requests/new' },
    } : {
      subcopy: 'Respond to an urgent request near you, or find a donation camp to join.',
      primary: { label: '🩸 Find requests near me', to: '/requests' },
      secondary: { label: '+ Post a Request', to: '/requests/new' },
    };
    if (user.role === 'recipient') return {
      subcopy: 'Post an urgent request — or start donating yourself. One account does both.',
      primary: { label: '🩸 Become a Donor', onClick: upgradeToDonor },
      secondary: { label: '+ Post a Request', to: '/requests/new' },
    };
    if (user.role === 'organization') return {
      subcopy: 'Run a donation camp, or post an urgent request on behalf of a patient.',
      primary: { label: 'Go to dashboard', to: '/org/dashboard' },
      secondary: { label: '+ Post a Request', to: '/requests/new' },
    };
    return {
      subcopy: 'Verify organizations, manage users, and keep an eye on the platform.',
      primary: { label: 'Admin dashboard', to: '/admin' },
      secondary: { label: 'Browse requests', to: '/requests' },
    };
  })();

  // Mirrors the hero's role handling: what a visitor should do when there's
  // nothing to respond to depends on who they already are.
  const emptyStateCta = (() => {
    if (!user) return {
      text: 'Register as a donor and we\u2019ll alert you the moment someone nearby needs your blood type.',
      label: '\ud83e\ude78 Become a Donor',
      to: '/register?role=donor',
    };
    if (user.role === 'donor') return {
      text: donorProfileMissing
        ? 'Finish your donor profile so we can alert you the moment a nearby request appears.'
        : 'You\u2019ll be alerted by SMS and email the moment a matching request is posted near you.',
      label: 'Complete my profile',
      to: donorProfileMissing ? '/donor/setup' : null,
    };
    if (user.role === 'recipient') return {
      text: 'Need blood for someone? Post a request and we\u2019ll alert every matching donor nearby.',
      label: '+ Post a Request',
      to: '/requests/new',
    };
    return { text: null, label: null, to: null };
  })();

  const heroButtonBase = {
    padding: isMobile ? '11px 20px' : '13px 26px', borderRadius: '9px',
    fontWeight: 700, fontSize: '14px', cursor: 'pointer',
    fontFamily: 'var(--font)', letterSpacing: '-0.01em',
  };
  const heroPrimaryStyle = {
    ...heroButtonBase,
    border: 'none', background: 'white', color: 'var(--red)',
    boxShadow: '0 4px 14px rgba(0,0,0,0.15)', transition: 'transform 0.15s',
  };
  const heroSecondaryStyle = {
    ...heroButtonBase,
    border: '1.5px solid rgba(255,255,255,0.35)',
    background: 'rgba(255,255,255,0.14)', color: 'white',
  };

  const PLATFORM_STATS = stats ? [
    { value: stats.total_donors, suffix: '+', label: 'Registered Donors' },
    { value: stats.requests_fulfilled, suffix: '', label: 'Requests Fulfilled' },
    { value: stats.total_camps, suffix: '', label: 'Donation Camps' },
    { value: stats.cities_covered, suffix: '', label: 'Cities Covered' },
  ] : [];

  // Section order depends on who's looking. A signed-in donor came here to find
  // requests, so requests lead. A first-time visitor has no idea what this site
  // is yet — leading with two data cards buries the pitch, the <h1>, and the
  // sign-up CTA below the fold.
  //
  // Decided from the stored token rather than `user`, because `user` is null
  // until /auth/me resolves: keying off it would render requests-first, then
  // shuffle the whole page under a returning visitor a beat later. The token's
  // presence tells us which order they'll end up in on the very first paint.
  // (NotificationContext reads the key the same way, for the same reason.)
  const [heroFirst] = useState(() => !localStorage.getItem('bb_token'));

  const requestsSection = (
    <>
      {/* ── Live requests ── */}
      <Reveal>
        <Card style={{ marginBottom: isMobile ? '12px' : '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'var(--red)', display: 'inline-block', animation: 'bb-pulse 2s ease-in-out infinite' }} />
              <span style={{ fontSize: '15px', fontWeight: 700, color: 'var(--gray-900)' }}>Live Requests</span>
            </div>
            <Link to="/requests" style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--red)', display: 'flex', alignItems: 'center', gap: '3px' }}>
              View all →
            </Link>
          </div>
          {requestsLoading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {[0, 1, 2].map(i => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '11px 10px' }}>
                  <Skeleton width="44px" height="28px" radius="8px" />
                  <div style={{ flex: 1 }}>
                    <Skeleton width="55%" height="13px" style={{ marginBottom: '6px' }} />
                    <Skeleton width="35%" height="11px" />
                  </div>
                  <Skeleton width="64px" height="26px" radius="20px" />
                </div>
              ))}
            </div>
          ) : requestsError ? (
            <div style={{ textAlign: 'center', padding: '28px 0' }}>
              <p style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--gray-700)', marginBottom: '4px' }}>
                Couldn't load open requests.
              </p>
              <p style={{ fontSize: '12.5px', color: 'var(--gray-500)', marginBottom: '14px', lineHeight: 1.5 }}>
                This is a connection problem on our side, not an empty list — there may well be people who need blood right now.
              </p>
              <Button size="sm" variant="outline" onClick={loadRequests}>Try again</Button>
            </div>
          ) : liveRequests.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '28px 0' }}>
              <p style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--gray-700)', marginBottom: '4px' }}>
                No open requests right now.
              </p>
              {/* Nobody needs blood this minute is good news, not a dead end —
                  so the quiet moment recruits for the next one. Role-aware
                  because telling an existing donor to become a donor reads as
                  broken. */}
              {emptyStateCta.text && (
                <p style={{ fontSize: '12.5px', color: 'var(--gray-500)', marginBottom: emptyStateCta.to ? '14px' : 0, lineHeight: 1.5 }}>
                  {emptyStateCta.text}
                </p>
              )}
              {emptyStateCta.to && (
                <Link to={emptyStateCta.to}>
                  <Button size="sm" variant="primary">{emptyStateCta.label}</Button>
                </Link>
              )}
            </div>
          ) : liveRequests.map((r, i, arr) => (
            <div
              key={r.id}
              onMouseEnter={() => setHoveredId(r.id)}
              onMouseLeave={() => setHoveredId(null)}
              style={{
                display: 'flex', alignItems: 'center', gap: '12px',
                padding: '11px 10px',
                borderRadius: '8px',
                borderBottom: i < arr.length - 1 ? '1px solid var(--gray-100)' : 'none',
                background: hoveredId === r.id ? 'var(--gray-50)' : 'transparent',
                transition: 'background 0.15s',
              }}
            >
              <BloodTypeBadge type={r.blood_type} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--gray-900)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {r.hospital_name || (user ? '—' : 'Login to view hospital')}
                  </span>
                  <ComponentBadge component={r.component} />
                </div>
                <div style={{ fontSize: '12px', color: 'var(--gray-500)', marginTop: '1px' }}>
                  {r.city} · {r.units_needed} unit{r.units_needed > 1 ? 's' : ''}
                </div>
              </div>
              {!isMobile && <UrgencyBadge urgency={r.urgency} />}
              <Link to={`/requests/${r.id}`}>
                <Button size="sm" variant="outline">Respond</Button>
              </Link>
            </div>
          ))}
        </Card>
      </Reveal>

      {/* ── Blood types most needed right now (derived from real open requests) ── */}
      <Reveal>
        <Card style={{ marginBottom: isMobile ? '16px' : '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <span style={{ fontSize: '15px', fontWeight: 700, color: 'var(--gray-900)' }}>Most Needed Right Now</span>
            <span style={{ fontSize: '11.5px', color: 'var(--gray-400)', fontWeight: 500 }}>From open requests</span>
          </div>
          {requestsLoading ? (
            <Skeleton width="100%" height="60px" />
          ) : demandEntries.length === 0 ? (
            <p style={{ fontSize: '13px', color: 'var(--gray-400)', padding: '8px 0' }}>No urgent needs right now — check back soon.</p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: '14px' }}>
              {demandEntries.map(([type, count]) => {
                const pct = Math.round((count / maxDemand) * 100);
                const color = pct > 66 ? 'var(--red)' : pct > 33 ? 'var(--amber)' : 'var(--green)';
                return (
                  <div key={type}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: '12.5px', marginBottom: '6px' }}>
                      <strong style={{ color: 'var(--gray-800)', fontWeight: 700 }}>{type}</strong>
                      <span style={{ color, fontWeight: 700, fontSize: '12px' }}>{count} request{count > 1 ? 's' : ''}</span>
                    </div>
                    <div style={{ height: '6px', borderRadius: '4px', background: 'var(--gray-100)' }}>
                      <div style={{ height: '100%', borderRadius: '4px', width: `${pct}%`, background: color, transition: 'width 0.6s ease' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <p style={{ fontSize: '11.5px', color: 'var(--gray-400)', marginTop: '14px', lineHeight: 1.5 }}>
            Reflects currently open requests platform-wide — if you're eligible, please consider donating.
          </p>
        </Card>
      </Reveal>
    </>
  );

  const heroSection = (
    <>
      {/* ── Hero ── */}
      <div style={{
        background: 'linear-gradient(140deg, var(--red-deeper) 0%, var(--red-dark) 45%, var(--red) 100%)',
        borderRadius: 'var(--radius-lg)',
        padding: isMobile ? '32px 22px' : '52px 44px',
        color: 'white',
        marginBottom: isMobile ? '16px' : '24px',
        position: 'relative',
        overflow: 'hidden',
        animation: 'bb-fadeUp 0.7s ease both',
      }}>
        {/* Decorative circles */}
        <div style={{ position: 'absolute', right: '-70px', top: '-70px', width: '320px', height: '320px', borderRadius: '50%', background: 'rgba(255,255,255,0.07)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', right: '40px', bottom: '-90px', width: '220px', height: '220px', borderRadius: '50%', background: 'rgba(255,255,255,0.05)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', right: '160px', top: '10px', width: '100px', height: '100px', borderRadius: '50%', background: 'rgba(255,255,255,0.04)', pointerEvents: 'none' }} />
        {!isMobile && (
          <div style={{ position: 'absolute', right: '90px', top: '38px', animation: 'bb-float 4s ease-in-out infinite', pointerEvents: 'none' }}>
            <Droplets size={46} color="rgba(255,255,255,0.22)" strokeWidth={1.5} />
          </div>
        )}

        <div style={{ position: 'relative', zIndex: 1 }}>
          {/* Badge */}
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '7px',
            background: 'rgba(255,255,255,0.14)', borderRadius: '20px',
            padding: '5px 13px', marginBottom: '18px',
            border: '1px solid rgba(255,255,255,0.2)',
          }}>
            <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: 'rgba(255,255,255,0.9)', display: 'inline-block', animation: 'bb-pulse 2s ease-in-out infinite' }} />
            <span style={{ fontSize: '11px', fontWeight: 600, color: 'rgba(255,255,255,0.9)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              Blood Donation Platform
            </span>
          </div>

          <h1 style={{ fontSize: isMobile ? '30px' : '40px', fontWeight: 900, lineHeight: 1.1, letterSpacing: '-0.04em', marginBottom: '14px' }}>
            Every drop<br />saves a life.
          </h1>
          <p style={{ fontSize: isMobile ? '14px' : '15px', opacity: 0.8, lineHeight: 1.65, maxWidth: '420px', marginBottom: '26px' }}>
            {hero.subcopy}
          </p>

          {/* Kept mounted while the session resolves — hiding rather than
              unmounting keeps the hero from jumping on every page load. */}
          <div style={{
            display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '22px',
            visibility: authLoading ? 'hidden' : 'visible',
          }}>
            {[hero.primary, hero.secondary].map((cta, i) => {
              const style = i === 0 ? heroPrimaryStyle : heroSecondaryStyle;
              return cta.to ? (
                <Link key={cta.label} to={cta.to}>
                  <button style={style}>{cta.label}</button>
                </Link>
              ) : (
                <button
                  key={cta.label}
                  style={{ ...style, opacity: upgrading ? 0.7 : 1 }}
                  onClick={cta.onClick}
                  disabled={upgrading}
                >
                  {upgrading ? 'Switching…' : cta.label}
                </button>
              );
            })}
          </div>

          {upgradeError && (
            <div style={{
              fontSize: '12.5px', fontWeight: 600, color: 'white',
              background: 'rgba(0,0,0,0.22)', border: '1px solid rgba(255,255,255,0.25)',
              borderRadius: '8px', padding: '9px 12px', marginBottom: '22px', maxWidth: '420px',
            }}>
              {upgradeError}
            </div>
          )}

          <div style={{ display: 'flex', gap: isMobile ? '10px' : '18px', flexWrap: 'wrap' }}>
            {HERO_TRUST.map(t => (
              <span key={t} style={{ fontSize: '11.5px', fontWeight: 600, color: 'rgba(255,255,255,0.75)' }}>{t}</span>
            ))}
          </div>
        </div>
      </div>

      {/* ── Platform stats ── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)',
        gap: isMobile ? '8px' : '12px',
        marginBottom: isMobile ? '16px' : '24px',
      }}>
        {(stats ? PLATFORM_STATS : Array.from({ length: 4 })).map((s, i) => (
          <Reveal key={s?.label || i} delay={i * 80}>
            <div style={{
              background: 'var(--white)',
              border: '1px solid var(--gray-200)',
              borderTop: '3px solid var(--red)',
              borderRadius: 'var(--radius)',
              padding: isMobile ? '14px 12px' : '18px 16px',
              textAlign: 'center',
              boxShadow: 'var(--shadow-sm)',
            }}>
              {s ? (
                <>
                  <div style={{ fontSize: isMobile ? '22px' : '26px', fontWeight: 900, color: 'var(--red)', letterSpacing: '-0.04em', lineHeight: 1 }}>
                    <Counter value={s.value} suffix={s.suffix} />
                  </div>
                  <div style={{ fontSize: '11.5px', color: 'var(--gray-500)', fontWeight: 500, marginTop: '4px', lineHeight: 1.3 }}>{s.label}</div>
                </>
              ) : (
                <>
                  <Skeleton width="60%" height="26px" style={{ margin: '0 auto' }} />
                  <Skeleton width="80%" height="12px" style={{ margin: '8px auto 0' }} />
                </>
              )}
            </div>
          </Reveal>
        ))}
      </div>
    </>
  );

  return (
    <PageLayout>

      {/* Anonymous: pitch first, proof second. Signed in: the work first. */}
      {heroFirst ? (
        <>{heroSection}{requestsSection}</>
      ) : (
        <>{requestsSection}{heroSection}</>
      )}

      {/* ── How it works / trust ── */}
      <Reveal>
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '12px', marginBottom: '18px' }}>
            <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--gray-900)' }}>How HopeForSomeone works</div>
            <Link to="/how-it-works" style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--red)', flexShrink: 0 }}>
              Full walkthrough →
            </Link>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)',
            gap: isMobile ? '18px' : '20px',
          }}>
            {STEPS.map(({ Icon, title, desc }, i) => (
              <Reveal key={title} delay={i * 90}>
                <div style={{ display: 'flex', flexDirection: isMobile ? 'row' : 'column', gap: isMobile ? '14px' : '10px', alignItems: isMobile ? 'flex-start' : 'stretch' }}>
                  <div style={{
                    width: '38px', height: '38px', borderRadius: '10px', flexShrink: 0,
                    background: 'var(--red-light)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Icon size={18} color="var(--red-dark)" strokeWidth={2} />
                  </div>
                  <div>
                    <div style={{ fontSize: '13.5px', fontWeight: 700, color: 'var(--gray-900)', marginBottom: '3px' }}>{title}</div>
                    <div style={{ fontSize: '12px', color: 'var(--gray-500)', lineHeight: 1.5 }}>{desc}</div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </Card>
      </Reveal>

    </PageLayout>
  );
}
